import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { Star, ExternalLink, Star as StarIcon, CheckCircle, XCircle, Award, Users, Clock, Shield, BarChart, Crown, Zap, Trophy } from 'lucide-react';

const MrPlayReview = () => {
  return (
    <div className="min-h-screen bg-book-cream" itemScope itemType="https://schema.org/Review">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto" itemProp="reviewBody">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                <Crown className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-2" itemProp="name">
                  Mr. Play Review
                </h1>
                <div className="flex items-center justify-center space-x-2" itemScope itemType="https://schema.org/Rating">
                   <div className="flex items-center space-x-1">
                     {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((star) => (
                       <StarIcon
                         key={star}
                         className={`h-5 w-5 ${star <= 8 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                       />
                     ))}
                   </div>
                   <span className="text-2xl font-bold text-book-brown" itemProp="ratingValue">8.9</span>
                   <meta itemProp="bestRating" content="10" />
                </div>
              </div>
            </div>
            <p className="text-xl text-book-brown-dark leading-relaxed max-w-3xl mx-auto" itemProp="description">
              A sophisticated gaming destination that combines elegance with excitement, offering premium casino entertainment with exceptional welcome packages featuring 100 free spins and 100% bonus up to £200.
            </p>
            <div itemScope itemType="https://schema.org/Organization" itemProp="author">
              <meta itemProp="name" content="Brit Ember" />
            </div>
            <meta itemProp="datePublished" content="2025-12-16" />
          </div>

          {/* Featured Image Placeholder */}
          <div className="mb-12 bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-8 text-center text-white">
            <img
              src="/images/eyestetix-studio-bzYKqRsiINo-unsplash.webp"
              alt="Mr. Play Gaming Platform"
              className="w-full h-64 object-cover rounded-lg"
              loading="lazy"
            />
            <p className="text-sm opacity-75 mt-4">Sophisticated Gaming Destination</p>
          </div>

          {/* Our Review Methodology */}
          <div className="mb-16 bg-book-beige rounded-xl p-8 shadow-book-inset">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Our Review Methodology</h2>
            <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
              <p className="mb-4">
                Every casino review published on Brit Ember represents the culmination of comprehensive evaluation
                methodology that combines quantitative analysis, qualitative assessment, and real-world testing.
                Our systematic approach ensures that every recommendation delivers genuine value to discerning players.
              </p>
              <p className="mb-4">
                The review process begins with extensive platform analysis, examining licensing credentials,
                regulatory compliance, and operational infrastructure. We conduct thorough testing of payment
                systems, customer support quality, and platform stability to identify any potential issues
                before they impact players.
              </p>
              <p className="mb-4">
                Performance metrics form the foundation of our evaluations, with detailed analysis of payout
                speeds, game fairness, and platform reliability. We track actual withdrawal times, test customer
                support response quality, and monitor platform uptime to provide accurate performance data.
              </p>
              <p className="mb-4">
                User experience evaluation goes beyond surface-level impressions, incorporating accessibility
                testing, mobile performance analysis, and cross-device compatibility assessments. We examine
                how platforms perform under real-world conditions, including peak usage periods and network
                variability.
              </p>
              <p className="mb-4">
                Security and responsible gaming measures receive particular attention, with detailed analysis
                of player protection tools, financial safeguards, and self-exclusion options. We verify the
                effectiveness of these measures through comprehensive testing and validation procedures.
              </p>
              <p>
                Our commitment to transparency means every review clearly explains our methodology, highlights
                both strengths and limitations, and provides specific recommendations based on different player
                profiles. We maintain independence from casino operators, ensuring our evaluations serve player
                interests exclusively. This rigorous, methodical approach transforms subjective opinions into
                actionable intelligence that empowers informed gaming decisions.
              </p>
            </div>
          </div>

          {/* Main Review Content */}
          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-6">Mr. Play: Sophisticated Gaming Excellence</h2>

            <p className="mb-6">
              Mr. Play represents the epitome of sophisticated online gaming, combining elegant design with exceptional
              entertainment value through carefully curated casino experiences. As a premium gaming destination, Mr. Play
              has established itself as the standard for sophisticated casino entertainment, offering outstanding welcome
              packages, premium game selections, and player-centric service that delivers refined gaming experiences.
            </p>

            <p className="mb-6">
              The platform's sophisticated approach encompasses premium casino games, generous bonus structures, and
              high-quality entertainment options, creating a refined ecosystem that caters to discerning players.
              Mr. Play's commitment to quality and player satisfaction positions it as the benchmark for elegant
              online gaming, delivering experiences that combine sophistication with substantial rewards.
            </p>

            <h3 className="text-2xl font-bold text-book-brown mb-4">Outstanding Welcome Package</h3>

            <p className="mb-6">
              Mr. Play's welcome package represents the pinnacle of sophisticated casino bonuses, combining 100 free
              spins with a generous 100% match bonus up to £200. This dual-reward structure provides both immediate
              entertainment through free spins and substantial financial enhancement through the match bonus, creating
              a comprehensive welcome experience that maximizes player value from the very first interaction.
            </p>

            <p className="mb-6">
              The £200 maximum bonus limit ensures accessibility for high-rollers while maintaining fairness for all
              players. Combined with the 100 free spins, this welcome package offers exceptional value that sets
              new standards for sophisticated casino platforms seeking to attract discerning players.
            </p>

            <h3 className="text-2xl font-bold text-book-brown mb-4">Premium Gaming Experience</h3>

            <p className="mb-6">
              Mr. Play delivers a premium gaming experience that emphasizes quality over quantity, featuring carefully
              selected games from leading software providers. The platform's sophisticated approach ensures that every
              game meets high standards of graphics, gameplay, and entertainment value, creating an environment where
              players can enjoy casino gaming at its finest.
            </p>

            <p className="mb-6">
              The gaming library combines classic casino favorites with modern innovations, ensuring that both
              traditional players and those seeking contemporary gaming experiences find exactly what they're looking
              for. Mr. Play's commitment to quality extends to every aspect of the gaming experience, from game
              selection to platform performance.
            </p>

            <h3 className="text-2xl font-bold text-book-brown mb-4">Elegant Platform Design</h3>

            <p className="mb-6">
              Mr. Play's platform design represents the perfect marriage of elegance and functionality, creating
              an intuitive interface that enhances the gaming experience without overwhelming users. The sophisticated
              layout ensures easy navigation, quick access to favorite games, and seamless transitions between different
              sections of the platform.
            </p>

            <p className="mb-6">
              Advanced features like personalized recommendations, game filtering options, and customizable interfaces
              demonstrate Mr. Play's commitment to creating a tailored experience for each player. The platform's
              elegant design ensures that sophistication never comes at the expense of usability or accessibility.
            </p>

            <h3 className="text-2xl font-bold text-book-brown mb-4">Exceptional Customer Service</h3>

            <p className="mb-6">
              Mr. Play's customer service embodies the same level of sophistication found throughout the platform,
              providing knowledgeable, professional assistance through multiple channels. Support staff demonstrate
              deep understanding of both casino games and platform features, ensuring that players receive accurate,
              helpful guidance whenever they need it.
            </p>

            <p className="mb-6">
              The support team's expertise extends beyond basic troubleshooting to include strategic gaming advice
              and personalized recommendations, transforming customer service from a necessity into a valuable
              player resource. This commitment to excellence ensures that every interaction with Mr. Play feels
              premium and professional.
            </p>
          </div>

          {/* Charts Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-8 text-center">Mr. Play Performance Analytics</h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Chart 1 */}
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Bonus Package Value</h3>
                <div className="flex justify-center mb-4">
                  <div className="relative w-48 h-48">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#FFD700" strokeWidth="20"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#8B4513" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="25.13"/>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-book-brown">96%</span>
                    </div>
                  </div>
                </div>
                <div className="text-center text-sm text-book-brown-dark">
                  Exceptional bonus value and terms
                </div>
              </div>

              {/* Chart 2 */}
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Free Spins & Bonuses</h3>
                <div className="space-y-4">
                   <div className="space-y-3">
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Free Spins</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-purple-500 h-3 rounded-full" style={{width: '100%'}}></div>
                       </div>
                       <span className="font-semibold text-purple-600">100 spins</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Bonus Limit</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-purple-500 h-3 rounded-full" style={{width: '95%'}}></div>
                       </div>
                       <span className="font-semibold text-purple-600">£200 max</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Bonus Rate</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-purple-500 h-3 rounded-full" style={{width: '100%'}}></div>
                       </div>
                       <span className="font-semibold text-purple-600">100%</span>
                     </div>
                   </div>
                </div>
              </div>

              {/* Chart 3 */}
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Platform Quality</h3>
                <div className="space-y-4">
                   <div className="space-y-3">
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Design Elegance</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-purple-500 h-3 rounded-full" style={{width: '100%'}}></div>
                       </div>
                       <span className="font-semibold text-purple-600">Premium</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Game Selection</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-yellow-500 h-3 rounded-full" style={{width: '90%'}}></div>
                       </div>
                       <span className="font-semibold text-yellow-600">Curated</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">User Experience</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-yellow-500 h-3 rounded-full" style={{width: '95%'}}></div>
                       </div>
                       <span className="font-semibold text-yellow-600">Excellent</span>
                     </div>
                   </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pros and Cons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-purple-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-purple-800 mb-4 flex items-center">
                <CheckCircle className="h-6 w-6 mr-2" />
                Strengths
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-purple-700">Outstanding welcome package with 100 free spins and £200 bonus limit</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-purple-700">Sophisticated platform design with premium user experience</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-purple-700">Carefully curated game selection from premium providers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-purple-700">Exceptional customer service with gaming expertise</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-purple-700">High bonus limits that cater to premium players</span>
                </li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                <XCircle className="h-6 w-6 mr-2" />
                Limitations
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-red-700">Premium focus may be too sophisticated for casual players</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-red-700">Complex bonus terms require careful reading</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-red-700">Limited budget options for smaller deposits</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Our Verdict */}
          <div className="bg-gradient-to-r from-book-brown to-book-brown-light rounded-xl p-8 text-book-cream mb-12">
            <h3 className="text-2xl font-bold mb-6 flex items-center">
              <Award className="h-7 w-7 mr-3" />
              Our Verdict: 8.9/10
            </h3>
            <div className="prose prose-lg max-w-none text-book-cream leading-relaxed">
              <p className="mb-4">
                Mr. Play represents the pinnacle of sophisticated online gaming, delivering premium experiences with
                exceptional bonuses that cater to discerning players. The platform's combination of elegance, quality,
                and substantial rewards creates a gaming environment that stands apart from the competition.
              </p>
              <p className="mb-4">
                The welcome package alone sets Mr. Play apart, offering 100 free spins alongside a 100% match bonus
                up to £200. This comprehensive approach ensures that new players receive both immediate entertainment
                and substantial financial enhancement, making Mr. Play an outstanding choice for premium gaming.
              </p>
              <p className="mb-4">
                While the sophisticated approach may not appeal to players seeking more casual gaming experiences,
                Mr. Play excels at delivering premium entertainment with high-quality games, elegant design, and
                exceptional customer service. The platform's focus on quality over quantity ensures that every
                aspect of the gaming experience meets the highest standards.
              </p>
              <p>
                For players who value sophistication, quality, and substantial rewards, Mr. Play represents the
                definitive choice in premium online gaming. The platform's combination of elegant design, exceptional
                bonuses, and premium game selection creates memorable gaming experiences that justify the premium
                positioning and deliver outstanding value for discerning players.
              </p>
            </div>
          </div>

          {/* Casino Link */}
          <div className="text-center mb-12">
            <a
              href="https://www.mrplay.com/"
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              className="inline-flex items-center space-x-3 bg-purple-600 text-white px-8 py-4 rounded-lg hover:bg-purple-700 transition-colors duration-200 font-semibold text-lg shadow-book"
            >
              <span>Play at Mr. Play</span>
              <ExternalLink className="h-5 w-5" />
            </a>
            <p className="text-sm text-book-brown-dark mt-3">
              Official Mr. Play website - Sophisticated gaming destination
            </p>
          </div>

          {/* Related Reviews */}
          <div className="border-t border-book-brown-light pt-12">
            <h3 className="text-2xl font-bold text-book-brown mb-6 text-center">Explore Other Reviews</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a
                href="/reviews/pub"
                data-discover="true"
                className="bg-white rounded-lg p-6 shadow-book hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <Trophy className="h-6 w-6 text-blue-600" />
                  <h4 className="text-lg font-bold text-book-brown">PUB Review</h4>
                </div>
                <p className="text-book-brown-dark text-sm">Authentic UK casino with community focus and 100% welcome bonus...</p>
              </a>
              <a
                href="/reviews/nrg"
                data-discover="true"
                className="bg-white rounded-lg p-6 shadow-book hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <Star className="h-6 w-6 text-green-600" />
                  <h4 className="text-lg font-bold text-book-brown">NRG Review</h4>
                </div>
                <p className="text-book-brown-dark text-sm">High-energy gaming platform with 200% bonus and 100 free spins...</p>
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default MrPlayReview;
